import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useQuotaQueryClient } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaQueryClient";
import { quotaQueryKeys } from "app/pages/sales-intelligence/modules/quota/queries/keys";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import type { CompanyInformationQuota } from "../types";

/**
 * Query for fetching company information quota
 *
 * @note Company information quota has no `DataCredits` equivalent.
 * It is used mostly to gate trial users for company analysis data.
 */
export const useCompanyInformationQuotaQuery = (enabled: boolean = true) => {
    const quotaQueryClient = useQuotaQueryClient();
    const existing = quotaQueryClient.getCompanyInformationQuotaFromCache();

    const isUnlimited = existing?.isUnlimited === true;
    const isQuotaReached = existing ? existing.remaining <= 0 : false;
    const queryEnabled = enabled && salesSettingsHelper.isSiUser() && !isUnlimited;

    return useQuery<CompanyInformationQuota>({
        queryKey: quotaQueryKeys.companyInformationQuota(),
        placeholderData: keepPreviousData,
        enabled: queryEnabled,
        staleTime: isUnlimited || isQuotaReached ? Infinity : 0,
        gcTime: isUnlimited || isQuotaReached ? Infinity : undefined,
        queryFn({ signal }) {
            return salesApiService.quota.fetchCompaniesQuota({ cancellation: signal });
        },
    });
};
